// =====================================================================
// Chamados — a BUSCA de equipamento na abertura do chamado.
//
// ⚠️⚠️ NÃO É ESPELHO DE NADA no backend. É regra de TELA: a lista de ativos vem
//    inteira do `GET /api/chamados/ativos?daLoja=`, e o recorte por termo
//    acontece aqui, no cliente.
//
// ---------------------------------------------------------------------
// POR QUE ISTO EXISTE
// ---------------------------------------------------------------------
// O seletor de equipamento era um `<select>` nativo com a loja inteira dentro.
// Medido na `dtbChamadoAtivo` em 28/08/2026: **165 a 189 opções por loja**.
// Rolar 165 linhas num tablet, com o equipamento quebrado na mão, é o momento em
// que se desiste do portal e se manda mensagem no WhatsApp.
//
// ---------------------------------------------------------------------
// A LÓGICA É PORTADA DO PRÓPRIO GPS-TI, E ISSO É DELIBERADO
// ---------------------------------------------------------------------
// O guia do Sebastião já resolve busca em português há tempo
// (`server/chamados/gps-ti/interno.html`, funções `semAcento`/`onde`), e a loja
// já está acostumada com o comportamento dele. Duas decisões vêm de lá:
//
//   1) A normalização remove ACENTO **e HÍFEN**. É o hífen que importa e que
//      ninguém lembra: sem ele, quem digita "nobreak" não acha "No-break" — e
//      `Nobreak` é exatamente uma das famílias do parque. O guia aprendeu isso
//      na prática; herdamos o conserto em vez de repetir o bug.
//
//   2) TODAS as palavras digitadas precisam aparecer, mas podem estar em CAMPOS
//      DIFERENTES. "impressora grill" acha a peça cuja FAMILIA é Impressora e a
//      DESCRICAO menciona Grill. Exigir tudo no mesmo campo devolveria vazio no
//      caso mais comum de todos.
//
// ⚠️ SEM DEBOUNCE, de propósito: são ~170 itens filtrados por tecla, sobre um
//    array já em memória. O guia faz o mesmo com 46 fichas. Debounce aqui só
//    acrescentaria atraso perceptível e um `useEffect` a mais para errar.
// =====================================================================

/**
 * Texto comparável: sem acento, sem hífen, minúsculo.
 *
 * ⚠️ O `‐-―` cobre os hífens tipográficos (‐ ‑ ‒ – —), não só o `-` do
 *    teclado: descrição vinda de planilha traz travessão, e ele quebraria a
 *    comparação exatamente como o hífen comum.
 */
export function semAcento(t) {
  return String(t == null ? '' : t)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-\u2010-\u2015]/g, '')
    .toLowerCase();
}

/** Os campos do ativo que a busca enxerga. */
const CAMPOS = ['FAMILIA', 'DESCRICAO', 'CODIGO', 'QUIOSQUE'];

/**
 * Recorta a lista de equipamentos pelo termo digitado.
 *
 * Termo vazio devolve a lista inteira — ⚠️ a MESMA referência não, uma cópia
 * não: devolve o próprio array recebido, porque quem chama só o lê.
 *
 * @param {Array} lista  ativos como o backend devolve (FAMILIA, DESCRICAO, …)
 * @param {string} termo o que a pessoa digitou
 */
export function filtrarAtivos(lista, termo) {
  if (!Array.isArray(lista)) return [];
  const palavras = semAcento(termo).split(/\s+/).filter(Boolean);
  if (!palavras.length) return lista;

  return lista.filter((a) => {
    // Um só texto por ativo: é o que permite a palavra 1 casar na FAMILIA e a
    // palavra 2 na DESCRICAO (ver decisão 2 no topo).
    const tudo = CAMPOS.map((k) => semAcento(a[k])).join(' ');
    return palavras.every((p) => tudo.includes(p));
  });
}

/**
 * Recorta pela família indicada pela ficha do guia.
 *
 * ⚠️⚠️ `familia` NULA ou desconhecida devolve a lista INTEIRA, nunca vazia. É a
 *    falha aberta que o de-para do backend (`gps-ti-fichas-familias.js`) assume:
 *    ficha nova, ficha ambígua ou entrada direta pela aba não filtram nada. Uma
 *    lista vazia faria o gerente concluir que a loja não tem equipamento
 *    cadastrado — e ele não teria como saber que está olhando um recorte.
 *
 * ⚠️ Casamento EXATO da família: os valores vêm dos dois lados do mesmo catálogo
 *    fechado (`chamados-familias.js`), então normalizar aqui só esconderia uma
 *    divergência que o teste de sanidade do de-para já impede.
 */
export function filtrarPorFamilia(lista, familia) {
  if (!Array.isArray(lista)) return [];
  if (!familia) return lista;
  const recorte = lista.filter((a) => a.FAMILIA === familia);
  // Família válida que a loja não tem (Servidor numa loja sem servidor
  // cadastrado): melhor a lista inteira do que uma tela vazia.
  return recorte.length ? recorte : lista;
}

/**
 * Recorta o parque pelo LUGAR escolhido em "Para onde é o chamado".
 *
 * ⚠️⚠️ ESTE RECORTE É ESTRITO — é o único dos três que NÃO falha aberto, e é
 *    decisão (Julia, 31/08/2026). Os outros dois recortam o que a pessoa NÃO
 *    pediu (a ficha do guia) ou o que ela digitou e vê na tela; este responde a
 *    uma pergunta que ela acabou de responder de propósito. Devolver o parque
 *    inteiro quando o CK não tem o equipamento seria desobedecer a escolha dela
 *    em silêncio — a tela avisa e oferece "ver todos", que é a saída explícita.
 *
 * ⚠️⚠️ VAZIO SIGNIFICA "A PRÓPRIA LOJA", NÃO "TUDO". É a mesma convenção do
 *    `normalizarQuiosque` no servidor, e é o que faz o filtro funcionar nos dois
 *    sentidos: escolher a loja esconde o parque dos CKs (medido em 31/08 — a MAL
 *    tem 165 itens de loja e 6 por CK, e os 24 dos quatro CKs poluíam a busca de
 *    quem estava no salão).
 *
 * ⚠️ Isto SÓ FUNCIONA porque a recarga de 27/08/2026 preencheu o `QUIOSQUE` do
 *    parque inteiro (2.277 ativos: 165 de loja + 6 por CK, em todas as 13
 *    lojas). Antes dela a coluna era quase toda nula e filtrar teria escondido o
 *    parque inteiro — está registrado como decisão contrária no comentário
 *    antigo do `ChamadosAbertura.js`, que esta função substitui.
 *
 * @param {Array} lista     ativos da loja, como o backend devolve
 * @param {string} quiosque o SUFIXO ('CK 3'), ou vazio para a própria loja
 */
export function filtrarPorQuiosque(lista, quiosque) {
  if (!Array.isArray(lista)) return [];
  const alvo = String(quiosque == null ? '' : quiosque).trim();
  // ⚠️ Compara sem caixa e com o espaço normalizado, como o `normalizarQuiosque`
  //    do servidor: 'ck 3' e 'CK  3' são o mesmo lugar.
  const chave = (s) => String(s == null ? '' : s).trim().toUpperCase().replace(/\s+/g, ' ');
  return lista.filter((a) => chave(a.QUIOSQUE) === chave(alvo));
}
